import { useQuery, UseQueryResult } from "@tanstack/react-query";

import { listManagedLiveReportLinks } from "../../client/manager/live-report";
import { LiveReportLink } from "../../models/live-report";
import { useTixologiTokenRefresher } from "./useTixologiTokenRefresher";

export const managedLiveReportLinksQueryKey = (partnerId: string | undefined) =>
  ["live-report", "manager", "links", partnerId] as const;

/**
 * Admin hook: lists live report links owned by the partner whose token comes
 * from `useTixologi({ id, isComedian })`.
 *
 * Internally retries once on 401/403 by refreshing the Tixologi token, so
 * stale-token errors after the cache TTL elapses are recovered transparently.
 */
export function useManagedLiveReportLinks(
  id: string | undefined,
  isComedian = false
): UseQueryResult<LiveReportLink[], Error> & { token: string | null } {
  const { token, refresh } = useTixologiTokenRefresher(id, isComedian);

  const query = useQuery({
    queryKey: managedLiveReportLinksQueryKey(id),
    queryFn: async () => {
      const initial = token ?? (await refresh());
      if (!initial) throw new Error("Tixologi token not available");
      return listManagedLiveReportLinks(initial, refresh);
    },
    enabled: !!token,
    staleTime: 30_000,
  });

  return Object.assign(query, { token });
}
